import CloseIcon from '@mui/icons-material/Close';
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useTheme
} from '@mui/material';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { SearchInput } from '../../../Form/InputSearch/input-search.component';
import { ListPricePoliciesUseCase } from '../../../../Usecase/PricePolicies/list-price-policies-usecase';
import { useToast } from '../../../../Hooks/ToasContext';
import { IPricePoliciesEntity } from '../../../../Services/api/price-policies/price-policies.interface';

interface ISelectPricePoliciesVariantProps {
    data?: any;
    onClose: () => void;
    onSearch?: (value: string) => void;
    onSelect: (values: IPricePoliciesEntity[]) => void;
    listPricePoliciesUseCase: ListPricePoliciesUseCase;
    selectedPolicies?: IPricePoliciesEntity[];
    multiple?: boolean;
}

const SelectPricePoliciesVariant: FC<ISelectPricePoliciesVariantProps> = ({ 
    data, 
    onClose, 
    onSearch, 
    onSelect, 
    listPricePoliciesUseCase,
    selectedPolicies = [],
    multiple = true
}) => {
    const [loading, setLoading] = useState(false);
    const [policies, setPolicies] = useState<IPricePoliciesEntity[]>([]);
    const [internalSelectedPolicies, setInternalSelectedPolicies] = useState<IPricePoliciesEntity[]>(selectedPolicies);
    const { showToast } = useToast();
    const theme = useTheme();

    const listPricePolicies = async ({ page, searchBy }: { page: number; searchBy?: string }) => {
        try {
            setLoading(true);
            
            const listResult = await listPricePoliciesUseCase.handle({ page, searchBy });

            if (listResult.isFailure) {
                showToast(String(listResult?.error?.error), 'error');
                return;
            }

            const response = listResult.getValue();

            if (response?.data?.length) {
                setPolicies(response.data);
            } else {
                setPolicies([]);
            }
        } catch (error) {
            console.log("error listPricePolicies function: ", error);
        } finally {
            setLoading(false);
        }
    };

    const handlePolicyToggle = (policy: IPricePoliciesEntity) => {
        if (multiple) {
            const isSelected = internalSelectedPolicies.some(p => p.id === policy.id);
            
            if (isSelected) {
                setInternalSelectedPolicies(prev => prev.filter(p => p.id !== policy.id));
            } else {
                setInternalSelectedPolicies(prev => [...prev, policy]);
            }
        } else {
            // Para seleção única, substitui a seleção atual
            setInternalSelectedPolicies([policy]);
        }
    };

    const handleSelectAll = () => {
        if (internalSelectedPolicies.length === policies.length) {
            setInternalSelectedPolicies([]);
        } else {
            setInternalSelectedPolicies([...policies]);
        }
    };

    const handleConfirm = () => {
        onSelect(internalSelectedPolicies);
        onClose();
    };

    const isPolicySelected = (policyId: string) => {
        return internalSelectedPolicies.some(p => p.id === policyId);
    };

    useEffect(() => {
        listPricePolicies({ page: 1 });
    }, []);

    useEffect(() => {
        setInternalSelectedPolicies(selectedPolicies);
    }, [selectedPolicies]);

    return (
        <Dialog
            open
            keepMounted
            onClose={onClose}
            aria-describedby="select-price-policies-dialog"
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">
                        {multiple ? 'Selecionar Políticas de Preço' : 'Selecionar Política de Preço'}
                    </Typography>
                    {multiple && (
                        <Typography variant="body2" color="textSecondary">
                            {internalSelectedPolicies.length} selecionada(s)
                        </Typography>
                    )}
                </Box>
            </DialogTitle>
            
            <DialogContent>
                <Box mb={2}>
                    <SearchInput
                        sx={{ width: '100%' }}
                        placeholder="Busque pelo nome da política de preço..."
                        onSearch={listPricePolicies}
                    />
                </Box>

                {multiple && policies.length > 0 && (
                    <Box mb={2}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={internalSelectedPolicies.length === policies.length && policies.length > 0}
                                    indeterminate={internalSelectedPolicies.length > 0 && internalSelectedPolicies.length < policies.length}
                                    onChange={handleSelectAll}
                                />
                            }
                            label="Selecionar todas"
                        />
                    </Box>
                )}

                <TableContainer component={Paper} variant="outlined">
                    <Table>
                        <TableHead>
                            <TableRow>
                                {multiple && (
                                    <TableCell padding="checkbox">
                                        Seleção
                                    </TableCell>
                                )}
                                <TableCell>Nome</TableCell>
                                <TableCell>Descrição</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Regras</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={multiple ? 5 : 4} align="center">
                                        <Typography>Carregando...</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : policies.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={multiple ? 5 : 4} align="center">
                                        <Typography color="textSecondary">
                                            Nenhuma política de preço encontrada
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                policies.map((policy) => (
                                    <TableRow 
                                        key={policy.id}
                                        hover
                                        onClick={() => handlePolicyToggle(policy)}
                                        sx={{ 
                                            cursor: 'pointer',
                                            backgroundColor: isPolicySelected(policy.id) 
                                                ? theme.palette.action.selected 
                                                : 'inherit'
                                        }}
                                    >
                                        {multiple && (
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    checked={isPolicySelected(policy.id)}
                                                    onChange={() => handlePolicyToggle(policy)}
                                                />
                                            </TableCell>
                                        )}
                                        <TableCell>
                                            <Typography variant="body2" fontWeight="medium">
                                                {policy.name}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="textSecondary">
                                                {policy.description || 'Sem descrição'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Box
                                                component="span"
                                                sx={{
                                                    px: 1,
                                                    py: 0.5,
                                                    borderRadius: 1,
                                                    fontSize: '0.75rem',
                                                    fontWeight: 'medium',
                                                    backgroundColor: policy.isActive 
                                                        ? theme.palette.success.light 
                                                        : theme.palette.grey[300],
                                                    color: policy.isActive 
                                                        ? theme.palette.success.contrastText 
                                                        : theme.palette.text.secondary,
                                                }}
                                            >
                                                {policy.isActive ? 'Ativa' : 'Inativa'}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {policy.rules?.length || 0} regra(s)
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </DialogContent>
            
            <DialogActions>
                <Button onClick={onClose} color="inherit">
                    Cancelar
                </Button>
                <Button 
                    onClick={handleConfirm} 
                    variant="contained"
                    disabled={internalSelectedPolicies.length === 0}
                >
                    {multiple 
                        ? `Selecionar ${internalSelectedPolicies.length} Política(s)` 
                        : 'Selecionar'
                    }
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SelectPricePoliciesVariant;
